import { useEffect, useRef } from "react";
import { Box } from "@mui/material";

interface HorizontalResizerProps {
  /** Identifies this resizer; compared against `resizingKey`. */
  resizerKey: string;
  /** Key of the resizer currently being dragged, or null when idle. */
  resizingKey: string | null;
  setResizingKey: (key: string | null) => void;
  /** Receives the panel's new width (in px) as the divider is dragged. */
  onResize: (width: number) => void;
}

/**
 * A draggable divider pinned to the right edge of a panel. While dragged it
 * reports the panel's new width via `onResize`. Resize-only port of apps/web's
 * HorizontalResizer.
 */
export default function HorizontalResizer({
  resizerKey,
  resizingKey,
  setResizingKey,
  onResize,
}: HorizontalResizerProps) {
  const startRef = useRef({ x: 0, width: 0 });
  // Held in a ref so the drag effect depends only on `resizing` and binds the
  // document listeners exactly once per drag rather than on every render.
  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;

  const resizing = resizingKey === resizerKey;

  const onMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (resizingKey) return;
    const panel = event.currentTarget.parentElement;
    startRef.current = { x: event.clientX, width: panel?.offsetWidth ?? 0 };
    setResizingKey(resizerKey);
  };

  useEffect(() => {
    if (!resizing) return;

    const onMouseMove = (event: MouseEvent) => {
      const deltaX = event.clientX - startRef.current.x;
      onResizeRef.current(startRef.current.width + deltaX);
    };
    const onMouseUp = () => setResizingKey(null);

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [resizing, setResizingKey]);

  return (
    <Box
      data-testid={`datasetPanel.resizer.${resizerKey}`}
      onMouseDown={onMouseDown}
      sx={{
        position: "absolute",
        top: 0,
        bottom: 0,
        right: 0,
        width: 10,
        zIndex: 600,
        cursor: "col-resize",
        // The visible divider sits on the right edge; the rest of the width is
        // an invisible grab area.
        borderRight: 2,
        borderColor: resizing ? "primary.main" : "divider",
        transition: "border-color 120ms",
        "&:hover": { borderColor: "primary.main" },
      }}
    />
  );
}
